import React from 'react';
import { useTranslation } from 'react-i18next';
import { CadrageT } from 'routes/BudgetModule/config/types';
import Alert from 'ui/Alert/Alert';
import SimpleModal from 'ui/Modals/SimpleModal';
import CadrageGaugeChart from './CadrageGaugeChart';
import { Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { translateBlockValue } from 'i18n';
import { CHAPTER_SOFTWARE_ID } from 'routes/BudgetModule/config/constants';

export type ChapterAmountT = {
    chapter: string;
    amount: number;
};

type Props = {
    isModalShown: boolean;
    setShowModal: DispatchState<boolean>;
    hasOrphanInscriptions: boolean;
    cadrageData: CadrageT[];
    amountByChapters: ChapterAmountT[];
    amountFieldLabel: string;
    reverseColor: boolean;
};

export default function CadrageChapterDetailsModal({
    isModalShown,
    setShowModal,
    hasOrphanInscriptions,
    cadrageData,
    amountByChapters,
    amountFieldLabel,
    reverseColor,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery<Theme>((theme) =>
        theme.breakpoints.down('sm'),
    );
    const gaugeSize = isSmallScreen ? 150 : 300;

    const chapters = new Set([
        ...amountByChapters.map((item) => item.chapter),
        ...cadrageData.map((cadrage) => cadrage.chapter),
    ]);

    const sortedChapters = Array.from(chapters).sort();

    return (
        <SimpleModal
            open={isModalShown}
            onClose={() => {
                setShowModal(false);
            }}
            maxWidth="lg"
            showCloseIcon={true}
            dialogTitle={t('budgetProposal.cadrageDetailsModalTitle', {
                chapter: translateBlockValue(
                    CHAPTER_SOFTWARE_ID,
                    true,
                ).toLowerCase(),
            })}
        >
            <>
                {hasOrphanInscriptions && (
                    <Alert severity="info">
                        {t('budgetProposal.cadrageOrphanInscriptionWarning')}
                    </Alert>
                )}
                <Grid container rowSpacing={2} sx={{ mt: '20px' }}>
                    {sortedChapters.map((chapter) => {
                        const amount =
                            amountByChapters.find(
                                (item) => item.chapter === chapter,
                            )?.amount || 0;
                        const cadrageAmount =
                            cadrageData.find((item) => item.chapter === chapter)
                                ?.amount || 0;
                        return (
                            <Grid
                                container
                                item
                                direction="column"
                                xs={4}
                                key={chapter}
                                sx={{ alignItems: 'center' }}
                            >
                                <Typography variant="h4">{chapter}</Typography>
                                <CadrageGaugeChart
                                    cadrageValue={cadrageAmount}
                                    data={{
                                        metric: amountFieldLabel,
                                        value: amount,
                                    }}
                                    height={gaugeSize}
                                    width={gaugeSize}
                                    reverseColor={reverseColor}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </>
        </SimpleModal>
    );
}
